import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/** Collapse Quill’s empty document to '' for cleaner API payloads. */
export function normalizeQuillHtml(html) {
  if (!html || typeof html !== 'string') return '';
  const t = html.trim();
  if (!t) return '';
  if (/^<p><br\s*\/?><\/p>$/i.test(t)) return '';
  if (/^<p>\s*<\/p>$/i.test(t)) return '';
  return html;
}

function readEditorHtml(quill) {
  return normalizeQuillHtml(quill.root.innerHTML);
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  editorMinHeight = '12rem',
  className = '',
}) {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const silentRef = useRef(false);
  onChangeRef.current = onChange;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || quillRef.current) return;

    const container = document.createElement('div');
    host.appendChild(container);

    const quill = new Quill(container, {
      theme: 'snow',
      placeholder: placeholder || '',
      modules: {
        toolbar: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote'],
          ['link'],
          ['clean'],
        ],
      },
    });

    quillRef.current = quill;

    const emit = () => {
      if (silentRef.current) return;
      onChangeRef.current(readEditorHtml(quill));
    };

    quill.on('text-change', emit);

    const initial = normalizeQuillHtml(value || '');
    if (initial) {
      silentRef.current = true;
      quill.setContents(quill.clipboard.convert({ html: initial }), 'silent');
      silentRef.current = false;
    }

    return () => {
      quill.off('text-change', emit);
      quillRef.current = null;
      host.innerHTML = '';
    };
    // Intentionally run once: Quill is created here; `value` is synced in the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only init
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const next = normalizeQuillHtml(value || '');
    const cur = readEditorHtml(quill);
    if (next === cur) return;
    silentRef.current = true;
    if (!next) {
      quill.setText('');
    } else {
      quill.setContents(quill.clipboard.convert({ html: next }), 'silent');
    }
    silentRef.current = false;
  }, [value]);

  return (
    <div
      ref={hostRef}
      className={`rich-text-editor rounded-lg border border-neutral-300 bg-white [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-neutral-200 [&_.ql-container]:rounded-b-lg [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[var(--rte-min)] [&_.ql-editor]:text-sm [&_.ql-editor]:text-neutral-900 ${className}`}
      style={{ '--rte-min': editorMinHeight }}
    />
  );
}
